/**
 * useMainConfig.ts
 */
import Focus from '@shared/types/enums/focus';
import MainProps from '@shared/types/props/main';
import Rail from '@shared/classes/rail';
import Slider from '@shared/classes/slider';
import useStableValue from '@shared/components/utils/hooks/useStableValue';
import { appSelector } from '@shared/store/slices/app.slice';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { MainConfig, MainTimeline } from '@shared/types/interfaces/main';
import { MutableRefObject, useMemo } from 'react';
import { THRESHOLD } from '@shared/types/constants/main';

interface MainConfigProps {
  channels: Rail | undefined;
  data: unknown[];
  filter: Rail | undefined;
  flags: MainProps['flags'];
  focus: ReturnType<typeof focusSelector>['focus'];
  message: string | undefined;
  rails: Rail[] | undefined;
  selection: string;
  slidersRef: MutableRefObject<{ id: string; data: Slider[] | undefined }>;
  stack: ReturnType<typeof focusSelector>['stack'];
  status: ReturnType<typeof appSelector>['status'];
  threshold: MainProps['threshold'];
  timelineRef: MutableRefObject<MainTimeline | null>;
}

/**
 * Field-by-field compare so `useStableValue` can skip handing consumers a
 * new object identity when nothing they'd actually read has changed;
 * `timeline` uses reference equality since it's expected to change every
 * tick on EPG pages.
 */
function isConfigEqual(a: MainConfig, b: MainConfig): boolean {
  return (
    a.display.channels === b.display.channels &&
    a.display.description === b.display.description &&
    a.display.filter === b.display.filter &&
    a.display.message === b.display.message &&
    a.display.rails === b.display.rails &&
    a.display.sliders === b.display.sliders &&
    a.display.search === b.display.search &&
    a.spinner?.type === b.spinner?.type &&
    a.threshold.front === b.threshold.front &&
    a.threshold.rear === b.threshold.rear &&
    a.timeline === b.timeline
  );
}

/**
 * Computes the display configuration for the Main sections (channels,
 * filter, rails, sliders, description, message) plus spinner/threshold/
 * timeline, and stabilizes it by value so `MainContext` consumers don't
 * re-render on every keypress when nothing they read actually changed.
 */
export default function useMainConfig({
  channels,
  data,
  filter,
  flags,
  focus,
  message,
  rails,
  selection,
  slidersRef,
  stack,
  status,
  threshold,
  timelineRef,
}: MainConfigProps) {
  const config = useMemo(() => {
    // Selected sections
    const isSlidersSelected = selection === slidersRef.current?.id;
    const isFilterSelected = selection === filter?.id;
    const isChannelsSelected = selection === channels?.id;

    // Display sections
    const displayMessage =
      (flags?.complete &&
        !rails?.length &&
        !slidersRef.current?.data?.length) ||
      !!message;
    const displayChannels =
      !!channels?.data?.length &&
      (isChannelsSelected || isFilterSelected || isSlidersSelected);
    const displayFilter =
      !!filter?.data?.length &&
      (flags?.filter?.fixed ||
        isFilterSelected ||
        (channels?.data?.length && isChannelsSelected) ||
        isSlidersSelected);
    const displayRails = !!rails?.length;
    const displaySliders =
      displayRails &&
      slidersRef.current?.data !== undefined &&
      (isSlidersSelected || isFilterSelected) &&
      !status?.description;
    const displayDescription =
      (flags?.complete &&
        !flags.loading &&
        !displaySliders &&
        !stack.includes(Focus.MENU) &&
        focus !== Focus.LOGIN &&
        (focus === Focus.RAILS_LIST ||
          focus === Focus.CHANNELS_RAIL ||
          focus === Focus.FLOATING_MENU ||
          focus === Focus.MODAL_BUTTONS ||
          focus === Focus.MODAL_INPUT ||
          (focus === Focus.FILTER_RAIL &&
            slidersRef.current?.data === undefined))) ??
      false;

    // Populate config
    return {
      display: {
        channels: displayChannels,
        description: displayDescription,
        filter: displayFilter,
        message: displayMessage,
        rails: displayRails,
        sliders: displaySliders,
        search: flags?.search,
      },
      spinner: {
        type: timelineRef?.current ? 'epg' : 'rails',
      },
      threshold: {
        front: threshold?.front ?? THRESHOLD.DEFAULT.FRONT,
        rear: threshold?.rear ?? THRESHOLD.DEFAULT.REAR,
      },
      timeline: timelineRef.current ?? undefined,
    };
  }, [
    data,
    flags,
    focus,
    rails,
    selection,
    slidersRef.current?.data,
    stack,
    status,
    threshold,
    timelineRef.current,
  ]);

  return useStableValue(config, isConfigEqual);
}
